import React, { useCallback, useEffect, useState } from 'react';
import { Switch, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import {
    autopilotModelRunEndpoint,
    autopilotModelRunPromoteEndpoint,
    autopilotModelRunsEndpoint,
    autopilotModelRunTargetEndpoint,
    autopilotModelRunUnpublishEndpoint,
} from '../../../api/endpoints';
import { MainContainer } from '../../../components/ContentWrapper';
import { WhiteBackground } from '../../../components/WhiteBackground';
import { ListPageHeader } from '../../../components/ListPageHeader/ListPageHeader';
import { DataTable, DataTableColumn } from '../../../components/DataTable/DataTable';
import { apiErrorCode } from '../../../utils/apiError';
import type { GateTableRow, ModelRun, ModelRunDetailResponse, ModelRunImportResponse, ModelRunsResponse, ModelScore } from '../../../types/autopilot';
import { ImportRunDialog } from './ImportRunDialog';

// PDP Autopilot prediction models (admin). Every nightly training run with
// its gate table: the champion (hier_bayes_v1) against the floors it must
// beat (heuristic_v0, pooled_prior_v2) and the challengers that are only
// reported (lgbm_quantile_v1, tabpfn_v1). A target is SHOWN to clients only
// when its switch is on; until then the endpoint's answers are shadow rows.
// "Import a run" / "Promote this run" do the Promote step by hand while the
// nightly execution is not allowed to (DL_ML_PROMOTE=0).

const MODEL_ORDER = ['hier_bayes_v1', 'pooled_prior_v2', 'heuristic_v0', 'lgbm_quantile_v1', 'tabpfn_v1'];
const MODEL_LABEL: Record<string, string> = {
    hier_bayes_v1: 'Champion · hierarchical Bayes',
    pooled_prior_v2: 'Floor · pooled priors',
    heuristic_v0: 'Floor · heuristic',
    lgbm_quantile_v1: 'Challenger · LightGBM',
    tabpfn_v1: 'Challenger · TabPFN',
};
const STATUS_CLASS: Record<string, string> = {
    promoted: 'border-[#2474DD] text-[#2474DD]',
    trained: 'border-border-token text-text-2',
    rolled_back: 'border-border-token text-text-2',
    unpublished: 'border-down text-down',
};

const n3 = (v: number | null) => (v === null || v === undefined ? '—' : v.toFixed(3));
const pct = (v: number | null) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
const when = (v: string | null) => (v ? dayjs(v).format('MMM D, YYYY HH:mm') : '—');

const serveTitle = (row: GateTableRow, canServe: boolean) => {
    if (!canServe) return 'Only a promoted run can be served';
    if (!row.passed_gate) return 'Did not pass the gate (turning it on forces it)';
    return row.served ? 'Shown to clients' : 'Shadow: logged, not shown';
};

const promoteErrorMessage = (code: string | null | undefined, detail: string) => {
    if (code === 'RunAlreadyPromoted') return 'This run is already on the endpoint';
    if (code === 'PromoteNotConfigured') return 'This API cannot promote: the image and SageMaker role are not configured';
    if (code === 'NoTargetPassedGate') return 'No target passed the gate, so the run was not promoted';
    if (code === 'ImportFailed') return detail || 'The promotion failed';
    return 'Could not promote the run';
};

const servedErrorMessage = (code: string | null | undefined) => {
    if (code === 'RunNotPromoted') return 'Only a promoted run can be served';
    if (code === 'TargetFailedGate') return 'This target did not pass the gate';
    return 'Could not update the target';
};

function StatusPill({ status }: { status: string }) {
    return (
        <span className={`font-bold uppercase border bg-surface text-[10.5px] rounded-md px-1.5 py-0.5 ${STATUS_CLASS[status] || STATUS_CLASS.trained}`}>
            {status.replace('_', ' ')}
        </span>
    );
}

// One target's gate table: every model's overall score side by side; the
// champion row carries the verdict.
function TargetGate({ row, run, onServed }: { row: GateTableRow; run: ModelRun; onServed: (key: string, served: boolean) => Promise<void> }) {
    const models = MODEL_ORDER.filter((m) => row.overall[m]);
    const champion = row.overall.hier_bayes_v1 || null;
    const canServe = run.status === 'promoted';
    const title = serveTitle(row, canServe);
    return (
        <div className="border border-border-token rounded-lg mb-3 overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-surface-2 border-b border-border-token">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[12.5px] font-semibold text-text-1 truncate">{row.target_key}</span>
                    <span className={`text-[10.5px] font-bold uppercase rounded-md px-1.5 py-0.5 border ${row.passed_gate ? 'border-up text-up' : 'border-border-token text-text-2'}`}>
                        {row.passed_gate ? 'passed gate' : 'failed gate'}
                    </span>
                    {row.masked_segments.length > 0 && (
                        <Tooltip title={row.masked_segments.join(', ')}>
                            <span className="text-[10.5px] text-text-2">{row.masked_segments.length} masked segment{row.masked_segments.length === 1 ? '' : 's'}</span>
                        </Tooltip>
                    )}
                </div>
                <Tooltip title={title}>
                    <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[11.5px] text-text-2">{row.served ? 'Shown' : 'Shadow'}</span>
                        <Switch size="small" checked={row.served} disabled={!canServe} onChange={(e) => onServed(row.target_key, e.target.checked)} />
                    </div>
                </Tooltip>
            </div>
            <table className="w-full text-[12.5px]">
                <thead>
                    <tr className="text-[10.5px] uppercase text-text-2">
                        <th className="text-left px-3 py-1.5">Model</th>
                        <th className="text-right px-3 py-1.5">n</th>
                        <th className="text-right px-3 py-1.5">MAE</th>
                        <th className="text-right px-3 py-1.5">Pinball p50</th>
                        <th className="text-right px-3 py-1.5">Pinball p25 / p75</th>
                        <th className="text-right px-3 py-1.5">Coverage (50% band)</th>
                    </tr>
                </thead>
                <tbody>
                    {models.map((m) => {
                        const s: ModelScore = row.overall[m];
                        const isChampion = m === 'hier_bayes_v1';
                        const beats = champion && !isChampion && champion.pinball_50 !== null && s.pinball_50 !== null && s.pinball_50 < champion.pinball_50;
                        return (
                            <tr key={m} className={isChampion ? 'bg-tint/40 font-semibold' : ''}>
                                <td className="px-3 py-1.5 text-text-1">
                                    {MODEL_LABEL[m] || m}
                                    {beats && <span className="ml-2 text-[10.5px] text-down font-semibold">beats champion</span>}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono">{s.n}</td>
                                <td className="px-3 py-1.5 text-right font-mono">{n3(s.mae)}</td>
                                <td className="px-3 py-1.5 text-right font-mono">{n3(s.pinball_50)}</td>
                                <td className="px-3 py-1.5 text-right font-mono">{n3(s.pinball_25)} / {n3(s.pinball_75)}</td>
                                <td className={`px-3 py-1.5 text-right font-mono ${s.coverage !== null && Math.abs(s.coverage - 0.5) > 0.1 ? 'text-down' : ''}`}>{pct(s.coverage)}</td>
                            </tr>
                        );
                    })}
                    {row.unseen_brands && row.unseen_brands.hier_bayes_v1 && (
                        <tr className="text-text-2">
                            <td className="px-3 py-1.5">Champion on brands unseen in training</td>
                            <td className="px-3 py-1.5 text-right font-mono">{row.unseen_brands.hier_bayes_v1.n}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{n3(row.unseen_brands.hier_bayes_v1.mae)}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{n3(row.unseen_brands.hier_bayes_v1.pinball_50)}</td>
                            <td className="px-3 py-1.5 text-right font-mono">—</td>
                            <td className="px-3 py-1.5 text-right font-mono">{pct(row.unseen_brands.hier_bayes_v1.coverage)}</td>
                        </tr>
                    )}
                </tbody>
            </table>
            {row.segments.filter((s) => s.model_name === 'hier_bayes_v1').length > 0 && (
                <details className="px-3 py-2 text-[12px] text-text-2">
                    <summary className="cursor-pointer">Champion by segment</summary>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {row.segments
                            .filter((s) => s.model_name === 'hier_bayes_v1')
                            .map((s) => {
                                const masked = row.masked_segments.includes(`${s.segment_kind}:${s.segment_value}`);
                                return (
                                    <span key={`${s.segment_kind}:${s.segment_value}`} className={`font-mono text-[11px] border rounded-md px-1.5 py-0.5 ${masked ? 'border-down text-down' : 'border-border-token'}`}>
                                        {s.segment_kind}:{s.segment_value} · n {s.n} · p50 {n3(s.pinball_50)} · {pct(s.coverage)}{masked ? ' · masked' : ''}
                                    </span>
                                );
                            })}
                    </div>
                </details>
            )}
        </div>
    );
}

export default function ModelRunsPage() {
    const [data, setData] = useState<ModelRunsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detail, setDetail] = useState<ModelRunDetailResponse | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [promoting, setPromoting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await axiosInstant.get<ModelRunsResponse>(autopilotModelRunsEndpoint(50));
            setData(body);
            setError(null);
            // first load only: pick the serving run; a row click must not refetch the list
            setSelectedId((cur) => (cur === null && body.runs.length ? (body.serving || body.runs[0]).id : cur));
        } catch (e) {
            setError('Failed to load model runs');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDetail = useCallback(async (id: number) => {
        setDetailLoading(true);
        try {
            const { data: body } = await axiosInstant.get<ModelRunDetailResponse>(autopilotModelRunEndpoint(id));
            setDetail(body);
        } catch (e) {
            toast.error('Failed to load the run');
        } finally {
            setDetailLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);
    useEffect(() => {
        if (selectedId !== null) loadDetail(selectedId);
    }, [selectedId, loadDetail]);

    const setServed = async (key: string, served: boolean) => {
        if (!detail) return;
        const target = detail.run.targets.find((t) => t.target_key === key);
        const force = served && target && !target.passed_gate;
        if (force && !window.confirm(`${key} did not pass the gate. Show the model's predictions to clients anyway?`)) return;
        try {
            await axiosInstant.patch(autopilotModelRunTargetEndpoint(detail.run.id, key), { served, force: !!force });
            toast.success(served ? `${key}: shown to clients` : `${key}: back to shadow`);
            await Promise.all([loadDetail(detail.run.id), load()]);
        } catch (e) {
            const code = apiErrorCode(e);
            toast.error(servedErrorMessage(code));
        }
    };

    const unpublish = async () => {
        if (!detail || !window.confirm(`Unpublish ${detail.run.run_key}? Every target goes back to shadow and clients see priors / heuristics.`)) return;
        try {
            await axiosInstant.post(autopilotModelRunUnpublishEndpoint(detail.run.id));
            toast.success('Run unpublished');
            await Promise.all([loadDetail(detail.run.id), load()]);
        } catch (e) {
            toast.error('Could not unpublish the run');
        }
    };

    const promote = async () => {
        if (!detail) return;
        const passed = detail.run.passed_count;
        const msg = passed
            ? `Promote ${detail.run.run_key} to the endpoint? ${passed} target${passed === 1 ? '' : 's'} passed the gate; the run serving now rolls back. Targets stay in shadow until switched on.`
            : `${detail.run.run_key} passed the gate for no target, so the API will refuse to promote it. Try anyway (the gate is re-read from S3)?`;
        if (!window.confirm(msg)) return;
        setPromoting(true);
        try {
            const { data: body } = await axiosInstant.post<ModelRunImportResponse>(autopilotModelRunPromoteEndpoint(detail.run.id), {});
            toast.success(`Promoted ${detail.run.run_key}: ${body.passed.length} target${body.passed.length === 1 ? '' : 's'} passed the gate`);
            await Promise.all([loadDetail(detail.run.id), load()]);
        } catch (e: any) {
            const d = e && e.response && e.response.data ? e.response.data.message : '';
            toast.error(promoteErrorMessage(apiErrorCode(e), d));
        } finally {
            setPromoting(false);
        }
    };

    const afterImport = async (runId: number | null) => {
        await load();
        if (runId !== null) setSelectedId(runId);
    };

    const secondary = (() => {
        if (!detail) return { label: undefined as string | undefined, onClick: undefined as (() => void) | undefined };
        if (detail.run.status === 'promoted') return { label: 'Unpublish this run', onClick: unpublish };
        return { label: promoting ? 'Promoting…' : 'Promote this run', onClick: promoting ? undefined : promote };
    })();

    const columns: DataTableColumn<ModelRun>[] = [
        { key: 'run', header: 'Run', render: (r) => <span className="font-mono text-[12px]">{r.run_key}</span> },
        { key: 'status', header: 'Status', render: (r) => <StatusPill status={r.status} /> },
        { key: 'trained', header: 'Trained', render: (r) => when(r.trained_at), className: 'text-text-2 text-[12px]' },
        { key: 'rows', header: 'Train / validate', className: 'text-right font-mono', render: (r) => `${r.n_train} / ${r.n_validate}` },
        { key: 'targets', header: 'Targets passed / shown', className: 'text-right font-mono', render: (r) => `${r.passed_count} / ${r.served_count} of ${r.targets.length}` },
    ];

    const serving = data ? data.serving : null;
    return (
        <MainContainer>
            <WhiteBackground>
                <ListPageHeader
                    title="Autopilot models"
                    subtitle={
                        serving
                            ? `Serving ${serving.run_key} · ${serving.served_count} of ${serving.targets.length} targets shown · ${data ? data.shadow_predictions : 0} shadow predictions logged`
                            : 'No promoted run. Clients see pooled priors and the heuristic table; every endpoint answer is logged as a shadow prediction.'
                    }
                    actionLabel="Import a run"
                    onActionClick={() => setImportOpen(true)}
                    secondaryActionLabel={secondary.label}
                    onSecondaryActionClick={secondary.onClick}
                />
                <ImportRunDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={afterImport} />
                <DataTable<ModelRun>
                    columns={columns}
                    rows={data ? data.runs : []}
                    loading={loading}
                    totalCount={data ? data.runs.length : 0}
                    hasMore={false}
                    error={error}
                    onLoadMore={() => undefined}
                    emptyMessage="No training runs yet. The nightly job writes one row per run once it is scheduled."
                    getRowKey={(r) => r.id}
                    onRowClick={(r) => setSelectedId(r.id)}
                />
                {detail && (
                    <div className="px-4 py-4">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="font-mono text-[13px] font-semibold">{detail.run.run_key}</span>
                            <StatusPill status={detail.run.status} />
                            <span className="text-[12px] text-text-2">
                                cutoff {when(detail.run.cutoff_at)} · gap {detail.run.gap_days} d · champion {detail.run.champion}
                                {detail.run.promoted_at ? ` · promoted ${when(detail.run.promoted_at)}` : ''}
                                {detail.run.shadow_predictions !== null ? ` · ${detail.run.shadow_predictions} shadow predictions` : ''}
                            </span>
                        </div>
                        {detailLoading && <div className="text-[12px] text-text-2 mb-2">Loading…</div>}
                        {detail.gate.length === 0 && !detailLoading && <div className="text-[12.5px] text-text-2">No targets had enough rows to score.</div>}
                        {detail.gate.map((row) => (
                            <TargetGate key={row.target_key} row={row} run={detail.run} onServed={setServed} />
                        ))}
                        {detail.run.notes && <pre className="text-[11.5px] text-text-2 whitespace-pre-wrap mt-2">{detail.run.notes}</pre>}
                    </div>
                )}
            </WhiteBackground>
        </MainContainer>
    );
}
